import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Mail, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface EmailConfig {
  id: string;
  user_id: string;
  email_address: string;
  display_name: string | null;
  is_primary: boolean;
  is_verified: boolean;
  bcc_to_personal: string | null;
  created_at: string;
  user?: {
    full_name: string;
    email: string;
  };
}

const EmailSettings = () => {
  const { user } = useAuth();
  const [emailConfigs, setEmailConfigs] = useState<EmailConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [appUsers, setAppUsers] = useState<{ id: string; full_name: string; email: string }[]>([]);

  // Form state
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [emailAddress, setEmailAddress] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bccToPersonal, setBccToPersonal] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  useEffect(() => {
    fetchEmailConfigs();
    fetchAppUsers();
  }, []);

  const fetchEmailConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from("recruiter_email_configs")
        .select(`
          *,
          user:app_users!recruiter_email_configs_user_id_fkey(full_name, email)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEmailConfigs((data as unknown as EmailConfig[]) || []);
    } catch (error) {
      console.error("Error fetching email configs:", error);
      toast.error("Failed to load email configurations");
    } finally {
      setLoading(false);
    }
  };

  const fetchAppUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("app_users")
        .select("id, full_name, email")
        .eq("is_active", true)
        .order("full_name");

      if (error) throw error;
      setAppUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleAddConfig = async () => {
    if (!selectedUserId || !emailAddress) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      // If setting as primary, unset other primary configs for this user
      if (isPrimary) {
        await supabase
          .from("recruiter_email_configs")
          .update({ is_primary: false })
          .eq("user_id", selectedUserId);
      }

      const { error } = await supabase.from("recruiter_email_configs").insert({
        user_id: selectedUserId,
        email_address: emailAddress.trim().toLowerCase(),
        display_name: displayName.trim() || null,
        bcc_to_personal: bccToPersonal.trim() || null,
        is_primary: isPrimary,
        is_verified: true, // Auto-verify for now
      });

      if (error) throw error;

      toast.success("Email configuration added");
      setDialogOpen(false);
      resetForm();
      fetchEmailConfigs();
    } catch (error: any) {
      console.error("Error adding email config:", error);
      toast.error(error.message || "Failed to add email configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async (id: string) => {
    try {
      const { error } = await supabase
        .from("recruiter_email_configs")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Email configuration deleted");
      fetchEmailConfigs();
    } catch (error) {
      console.error("Error deleting email config:", error);
      toast.error("Failed to delete email configuration");
    }
  };

  const handleSetPrimary = async (config: EmailConfig) => {
    try {
      // Unset other primaries for this user
      await supabase
        .from("recruiter_email_configs")
        .update({ is_primary: false })
        .eq("user_id", config.user_id);

      // Set this one as primary
      const { error } = await supabase
        .from("recruiter_email_configs")
        .update({ is_primary: true })
        .eq("id", config.id);

      if (error) throw error;

      toast.success("Primary email updated");
      fetchEmailConfigs();
    } catch (error) {
      console.error("Error setting primary:", error);
      toast.error("Failed to update primary email");
    }
  };

  const resetForm = () => {
    setSelectedUserId("");
    setEmailAddress("");
    setDisplayName("");
    setBccToPersonal("");
    setIsPrimary(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Email Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure email addresses for recruiters to send emails from the system
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Email Config
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Email Configuration</DialogTitle>
              <DialogDescription>
                Configure an email address for a recruiter to send emails from
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="user">Recruiter *</Label>
                <select
                  id="user"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">Select a recruiter...</option>
                  {appUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="recruiter@company.com"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  This email must be verified in your Resend domain
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  placeholder="John Smith"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bcc">BCC to Personal Email</Label>
                <Input
                  id="bcc"
                  type="email"
                  placeholder="personal@gmail.com"
                  value={bccToPersonal}
                  onChange={(e) => setBccToPersonal(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Optionally send a copy to the recruiter's personal email
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="primary"
                  checked={isPrimary}
                  onCheckedChange={setIsPrimary}
                />
                <Label htmlFor="primary">Set as primary email</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddConfig} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Configured Email Addresses
          </CardTitle>
          <CardDescription>
            Manage email addresses that recruiters can use to send emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailConfigs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No email configurations yet</p>
              <p className="text-sm">Add an email configuration to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Email Address</TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead>BCC To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emailConfigs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{config.user?.full_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {config.user?.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {config.email_address}
                        {config.is_primary && (
                          <Badge variant="secondary" className="text-xs">
                            Primary
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{config.display_name || "-"}</TableCell>
                    <TableCell>
                      {config.bcc_to_personal || (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {config.is_verified ? (
                        <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/20">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-500/20">
                          <AlertCircle className="mr-1 h-3 w-3" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!config.is_primary && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetPrimary(config)}
                          >
                            Set Primary
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteConfig(config.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email Integration Info</CardTitle>
          <CardDescription>
            How email sending works in this system
          </CardDescription>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none text-muted-foreground">
          <ul className="space-y-2">
            <li>
              Emails are sent via <strong>Resend</strong> API for reliable delivery
            </li>
            <li>
              Each recruiter can have multiple email addresses configured
            </li>
            <li>
              The <strong>primary</strong> email is used by default when sending
            </li>
            <li>
              Optional <strong>BCC</strong> ensures a copy goes to the recruiter's personal inbox
            </li>
            <li>
              All sent emails are logged in the candidate's communication history
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettings;